import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getLicenseDb, redirectToLicenseEndpoint, getCustomerId } from "./license";

const CACHE_KEY = "portal-license-cache";
// A license-service outage shouldn't lock a paying buyer out of their whole
// business tool — fall back to the last confirmed-good status for a while.
// Long enough to ride out a bad deploy/outage, short enough that blocking
// the license domain isn't a permanent free-access trick.
const CACHE_GRACE_MS = 72 * 60 * 60 * 1000; // 72h

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { status, cachedAt } = JSON.parse(raw);
    if (Date.now() - cachedAt > CACHE_GRACE_MS) return null;
    return status;
  } catch {
    return null;
  }
}

function writeCache(status) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ status, cachedAt: Date.now() }));
  } catch {
    // localStorage unavailable (private browsing etc.) — just skip caching
  }
}

const TIERS = [
  { label: "1–3 employees",    price: "$29/mo flat" },
  { label: "4–10 employees",   price: "+ $8.00 / employee" },
  { label: "11–25 employees",  price: "+ $6.00 / employee" },
  { label: "26–50 employees",  price: "+ $4.50 / employee" },
  { label: "51–100 employees", price: "+ $3.25 / employee" },
  { label: "100+ employees",   price: "Custom — contact us" },
];

export default function PortalAccessGate({ S, C, seatCount, children }) {
  const [status, setStatus]     = useState("loading"); // loading | active | past_due | inactive
  const [overCap, setOverCap]   = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy]         = useState(false);

  useEffect(() => {
    const applyFallback = () => setStatus(readCache() || "inactive");
    const timeout = setTimeout(applyFallback, 6000);
    const licenseDb = getLicenseDb();
    const unsub = onSnapshot(
      doc(licenseDb, "licenses", getCustomerId()),
      (snap) => {
        clearTimeout(timeout);
        const portal = snap.exists() ? snap.data()?.portal : null;
        const resolved = portal?.status === "active" || portal?.status === "past_due" ? portal.status : "inactive";
        setOverCap(!!portal?.overCap);
        setStatus(resolved);
        if (resolved !== "inactive") writeCache(resolved);
      },
      () => {
        clearTimeout(timeout);
        applyFallback();
      }
    );
    return () => { clearTimeout(timeout); unsub(); };
  }, []);

  const redirectTo = async (endpoint, extraBody) => {
    setBusy(true);
    const redirected = await redirectToLicenseEndpoint(endpoint, extraBody);
    if (!redirected) setBusy(false);
  };

  const startTrial     = () => redirectTo("createCheckoutSession", { product: "portal", seatCount, successUrl: window.location.href, cancelUrl: window.location.href });
  const manageBilling  = () => redirectTo("createPortalSession",  { returnUrl: window.location.href });

  if (status === "loading") {
    return (
      <div style={{ ...S.center, color:"#A8C0E8", fontSize:"14px" }}>
        Connecting…
      </div>
    );
  }

  if (status === "inactive") {
    return (
      <div style={S.center}>
        <div style={{ ...S.card, maxWidth:"460px" }}>
          <svg viewBox="0 0 700 240" style={{ height:"34px", width:"auto", display:"block", margin:"0 auto 18px" }}>
            <defs>
              <linearGradient id="pagBadgeGrad" x1="0" y1="0" x2="120" y2="120" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#173D6B"/>
                <stop offset="1" stopColor="#0A2647"/>
              </linearGradient>
            </defs>
            <g transform="translate(0,60)">
              <rect x="0" y="0" width="120" height="120" rx="27" fill="url(#pagBadgeGrad)"/>
              <rect x="28" y="28" width="46" height="46" rx="12" fill="#FFFFFF" fillOpacity="0.4"/>
              <rect x="42" y="42" width="46" height="46" rx="12" fill="#FFFFFF" fillOpacity="0.68"/>
              <rect x="56" y="56" width="34" height="34" rx="10" fill="#4C8DFF"/>
            </g>
            <text x="160" y="145" fontFamily="Poppins" fontWeight="500" fontSize="66" letterSpacing="0.5" fill="#132C4C">Custodial<tspan fontWeight="700" fill="#2F6BE0"> View</tspan></text>
          </svg>
          <div style={{ ...S.title, textAlign:"center" }}>Start your CustodialView portal subscription</div>
          <div style={{ ...S.sub, textAlign:"center" }}>
            14 days free, no card required. Priced per team member — the more your crew grows, the lower the per-seat rate.
          </div>
          <div style={{ border:`1px solid ${C.border}`, borderRadius:"10px", overflow:"hidden", marginBottom:"18px" }}>
            {TIERS.map((t, i) => (
              <div key={t.label} style={{
                display:"flex", justifyContent:"space-between", padding:"10px 14px", fontSize:"13px",
                background: i % 2 ? C.offWhite : C.white,
                borderTop: i === 0 ? "none" : `1px solid ${C.border}`,
              }}>
                <span style={{ color:C.textDark, fontWeight:"600" }}>{t.label}</span>
                <span style={{ color:C.textMid }}>{t.price}</span>
              </div>
            ))}
          </div>
          <button style={S.btn} onClick={startTrial} disabled={busy}>
            {busy ? "Redirecting…" : "Start free trial"}
          </button>
        </div>
        <a href="https://custodialview.com" target="_blank" rel="noopener noreferrer"
          style={{ position:"fixed", bottom:"14px", left:0, right:0, textAlign:"center",
            fontSize:"11px", color:"rgba(255,255,255,0.45)", textDecoration:"none" }}>
          <svg viewBox="0 0 120 120" width="14" height="14" style={{ display:"inline-block", verticalAlign:"-3px", marginRight:"5px" }}>
            <defs>
              <linearGradient id="pagFooterBadgeGrad" x1="0" y1="0" x2="120" y2="120" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#173D6B"/>
                <stop offset="1" stopColor="#0A2647"/>
              </linearGradient>
            </defs>
            <rect width="120" height="120" rx="27" fill="url(#pagFooterBadgeGrad)"/>
            <rect x="28" y="28" width="46" height="46" rx="12" fill="#FFFFFF" fillOpacity="0.4"/>
            <rect x="42" y="42" width="46" height="46" rx="12" fill="#FFFFFF" fillOpacity="0.68"/>
            <rect x="56" y="56" width="34" height="34" rx="10" fill="#4C8DFF"/>
          </svg>
          Powered by <b style={{ color:"rgba(255,255,255,0.75)" }}>CustodialView</b>
        </a>
      </div>
    );
  }

  return (
    <>
      {status === "past_due" && !dismissed && (
        <div style={{ background:C.red, color:C.white, padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"center", gap:"14px", fontSize:"13px", fontWeight:"600", flexWrap:"wrap", position:"sticky", top:0, zIndex:100 }}>
          <span>⚠️ Your last payment failed — update your billing to avoid losing access.</span>
          <button onClick={manageBilling} disabled={busy} style={{ background:C.white, color:C.red, border:"none", borderRadius:"6px", padding:"4px 12px", fontWeight:"700", fontSize:"12px", cursor:"pointer" }}>
            {busy ? "Loading…" : "Update billing"}
          </button>
          <button onClick={() => setDismissed(true)} style={{ background:"transparent", border:"none", color:C.white, cursor:"pointer", fontSize:"16px", padding:"0 4px", lineHeight:1 }}>×</button>
        </div>
      )}
      {overCap && (
        <div style={{ background:C.amber, color:C.textDark, padding:"8px 16px", textAlign:"center", fontSize:"12px", fontWeight:"600", position:"sticky", top:0, zIndex:99 }}>
          You've grown past 100 team members — contact us for custom enterprise pricing.
        </div>
      )}
      {children}
    </>
  );
}
